import React, { useState, useRef, useEffect } from "react";

interface Option {
  value: string;
  label: string;
}

interface CustomCheckboxSelectProps {
  label: string;
  name: string;
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: (e: any) => void;
  error?: string;
  options: Option[];
}

const CustomCheckboxSelect: React.FC<CustomCheckboxSelectProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  options = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleOptionClick = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabels = options
    .filter((opt) => value.includes(opt.value))
    .map((opt) => opt.label)
    .join(", ");

  return (
    <div className="flex flex-col w-full relative" ref={dropdownRef}>
      <label className="block text-lg font-medium text-[#DCDCDC] mb-2">
        {label}
      </label>

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onBlur={onBlur}
        className={`flex items-center justify-between w-full px-3 py-2 border-none rounded-md text-[#F4F5F7] bg-[#2E4051] focus:border-[#008564] focus:outline-none focus:ring-2 focus:ring-[#008564] ${
          error ? "border-red-500" : "border-gray-300"
        }`}
      >
        <span className={selectedLabels ? "" : "text-[#F4F5F7]"}>
          {selectedLabels || "Select..."}
        </span>
        <svg
          className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="scrollbar-hide absolute top-full left-0 right-0 z-10 mt-1 max-h-60 p-4 overflow-auto bg-[#2E4051] border border-gray-600 rounded-md shadow-lg">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => handleOptionClick(option.value)}
              className={`flex flex-row items-center gap-2 px-3 py-2 border-b-2 border-white cursor-pointer transition-colors ${
                value.includes(option.value)
                  ? "text-white"
                  : "hover:text-[#55B7E4] text-white"
              }`}
            >
              <div
                className={`flex h-5 w-5 border-2 border-white rounded items-center justify-center ${
                  value.includes(option.value) ? "bg-[#008564]" : ""
                }`}
              >
                {value.includes(option.value) && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
              <div>{option.label}</div>
            </div>
          ))}
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default CustomCheckboxSelect;
